import { lazy, Suspense, useEffect, useState, type FormEvent } from 'react'
import { ApiClientError } from '../../services/apiClient'
import {
  adminClient,
  EMAIL_TEMPLATE_LABELS,
  type EmailConfigResponse,
  type EmailConfigPayload,
  type EmailTemplate,
  type EmailTemplateKey,
} from '../../services/adminClient'

// Tiptap es pesado (~250 KB) — solo cargar cuando el admin abre la pestaña.
const EmailTemplateEditor = lazy(() =>
  import('../../components/RichText/EmailTemplateEditor').then((m) => ({
    default: m.EmailTemplateEditor,
  })),
)

/**
 * Pestaña "Envío de correo" del portal admin (pendiente #2).
 *
 * Dos sub-secciones:
 *   1. Configuración SMTP — host/port/user/password/encryption/from + botón "Enviar prueba".
 *   2. Plantillas — selector + editor Tiptap con variables atómicas + botón "Enviar prueba".
 */
export function EmailTab() {
  return (
    <div className="content-stack" style={{ gap: '1rem' }}>
      <SmtpSection />
      <TemplatesSection />
    </div>
  )
}

// ─── SMTP ──────────────────────────────────────────────────────────────────

function SmtpSection() {
  const [state, setState] = useState<EmailConfigResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Form fields. Password queda vacía al cargar (no se devuelve en claro).
  const [host, setHost] = useState('')
  const [port, setPort] = useState('587')
  const [user, setUser] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [encryption, setEncryption] = useState<'tls' | 'ssl' | 'none'>('tls')
  const [fromAddress, setFromAddress] = useState('')
  const [fromName, setFromName] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const [testTo, setTestTo] = useState('')
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<string | null>(null)
  const [testError, setTestError] = useState<string | null>(null)

  function hydrate(s: EmailConfigResponse) {
    setState(s)
    setHost(s.email['email.smtp_host']?.value ?? '')
    setPort(s.email['email.smtp_port']?.value ?? '587')
    setUser(s.email['email.smtp_user']?.value ?? '')
    setPassword('')  // siempre vacía (no exponer)
    const enc = (s.email['email.smtp_encryption']?.value ?? 'tls') as 'tls' | 'ssl' | ''
    setEncryption(enc === '' ? 'none' : enc)
    setFromAddress(s.email['email.from_address']?.value ?? '')
    setFromName(s.email['email.from_name']?.value ?? '')
  }

  useEffect(() => {
    let cancelled = false
    adminClient.serverConfig
      .showEmail()
      .then((s) => { if (!cancelled) hydrate(s) })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar.')
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSavedMessage(null)
    setSaveError(null)
    setSubmitting(true)

    const payload: EmailConfigPayload = {
      smtp_host: host,
      smtp_port: Number(port),
      smtp_user: user,
      smtp_encryption: encryption,
      from_address: fromAddress,
      from_name: fromName,
    }
    if (password.trim() !== '') payload.smtp_password = password

    try {
      const res = await adminClient.serverConfig.updateEmail(payload)
      hydrate(res)
      setSavedMessage(`Guardado · cambió: ${res.changed.join(', ') || 'nada'}`)
      setTimeout(() => setSavedMessage(null), 3500)
    } catch (err) {
      setSaveError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo guardar.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleTest() {
    setTestResult(null)
    setTestError(null)
    if (testTo.trim() === '') {
      setTestError('Indica un correo destino para la prueba.')
      return
    }
    setTesting(true)
    try {
      await adminClient.serverConfig.sendTestEmail(testTo.trim())
      setTestResult(`Correo de prueba enviado a ${testTo.trim()}.`)
    } catch (err) {
      setTestError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo enviar.')
    } finally {
      setTesting(false)
    }
  }

  if (loadError) return <section className="card"><p className="error">{loadError}</p></section>
  if (loading || !state) return <section className="card"><p className="muted">Cargando…</p></section>

  const passwordIsSet = state.email['email.smtp_password']?.is_set

  return (
    <section className="card">
      <h3>Configuración SMTP</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Servidor de salida para todos los correos de la plataforma (OTPs, recuperación, pagos).
      </p>

      <form className="form-grid" onSubmit={handleSubmit} noValidate style={{ marginTop: '1rem' }}>
        <div className="grid-2">
          <label className="field">
            <span>Servidor (host)</span>
            <input value={host} onChange={(e) => setHost(e.target.value)}
                   placeholder="smtp.example.com" disabled={submitting} className="mono" />
          </label>
          <label className="field">
            <span>Puerto</span>
            <input value={port} onChange={(e) => setPort(e.target.value)}
                   type="number" min={1} max={65535} disabled={submitting} className="mono" />
          </label>
        </div>

        <label className="field">
          <span>Usuario</span>
          <input value={user} onChange={(e) => setUser(e.target.value)}
                 placeholder="no-reply@velt.com.mx" disabled={submitting} className="mono" autoComplete="off" />
        </label>

        <label className="field">
          <span>Contraseña</span>
          {passwordIsSet && (
            <small className="muted">
              Actualmente configurada: <span className="mono">{state.email['email.smtp_password']?.value}</span>
            </small>
          )}
          <div className="password-row">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={passwordIsSet ? 'Dejar en blanco para mantener la actual' : ''}
              disabled={submitting}
              className="mono"
              autoComplete="new-password"
            />
            <button type="button" className="ghost-btn"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Ocultar' : 'Mostrar'}>
              {showPassword ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
        </label>

        <div className="grid-2">
          <label className="field">
            <span>Cifrado</span>
            <select value={encryption} onChange={(e) => setEncryption(e.target.value as 'tls' | 'ssl' | 'none')}
                    disabled={submitting}>
              <option value="tls">STARTTLS</option>
              <option value="ssl">SSL/TLS</option>
              <option value="none">Sin cifrado</option>
            </select>
          </label>
          <label className="field">
            <span>From (dirección)</span>
            <input value={fromAddress} onChange={(e) => setFromAddress(e.target.value)}
                   type="email" placeholder="no-reply@velt.com.mx" disabled={submitting} className="mono" />
          </label>
        </div>

        <label className="field">
          <span>From (nombre visible)</span>
          <input value={fromName} onChange={(e) => setFromName(e.target.value)}
                 placeholder="Velt Intrepid" disabled={submitting} />
        </label>

        {saveError && <p className="error">{saveError}</p>}
        {savedMessage && <p className="info">{savedMessage}</p>}

        <div className="modal__actions">
          <button type="submit" className="primary-btn" disabled={submitting}>
            {submitting ? 'Guardando…' : 'Guardar configuración'}
          </button>
        </div>
      </form>

      <hr style={{ margin: '1.5rem 0', borderColor: 'var(--border)' }} />

      <h4>Enviar correo de prueba</h4>
      <p className="muted" style={{ marginTop: 0 }}>
        Envía un correo simple usando la configuración guardada actualmente. Útil para validar antes
        de poner en uso.
      </p>
      <div className="form-grid">
        <label className="field">
          <span>Correo destino</span>
          <div className="password-row">
            <input value={testTo} onChange={(e) => setTestTo(e.target.value)}
                   type="email" placeholder="prueba@example.com" disabled={testing} className="mono" />
            <button type="button" className="ghost-btn" onClick={handleTest} disabled={testing}>
              {testing ? 'Enviando…' : 'Enviar prueba'}
            </button>
          </div>
        </label>
        {testError && <p className="error">{testError}</p>}
        {testResult && <p className="info">{testResult}</p>}
      </div>
    </section>
  )
}

// ─── Templates ─────────────────────────────────────────────────────────────

function TemplatesSection() {
  const [items, setItems] = useState<EmailTemplate[]>([])
  const [selectedKey, setSelectedKey] = useState<EmailTemplateKey>('signup_otp')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [subject, setSubject] = useState('')
  const [bodyHtml, setBodyHtml] = useState('<p></p>')
  const [submitting, setSubmitting] = useState(false)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const [testTo, setTestTo] = useState('')
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<string | null>(null)
  const [testError, setTestError] = useState<string | null>(null)

  const selected = items.find((t) => t.key === selectedKey) ?? null

  function reloadList() {
    setLoading(true)
    adminClient.emailTemplates
      .list()
      .then((res) => {
        setItems(res.items)
        const current = res.items.find((t) => t.key === selectedKey)
        if (current) {
          setSubject(current.subject)
          setBodyHtml(current.body_html)
        }
      })
      .catch((err) =>
        setLoadError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar.'),
      )
      .finally(() => setLoading(false))
  }
  useEffect(reloadList, [])

  function handleSelect(k: EmailTemplateKey) {
    setSelectedKey(k)
    const t = items.find((x) => x.key === k)
    if (t) {
      setSubject(t.subject)
      setBodyHtml(t.body_html)
    }
    setSavedMessage(null)
    setSaveError(null)
    setTestResult(null)
    setTestError(null)
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setSavedMessage(null)
    setSaveError(null)
    try {
      const res = await adminClient.emailTemplates.update(selectedKey, {
        subject,
        body_html: bodyHtml,
      })
      // Reemplaza el item en items[] con el actualizado.
      setItems((prev) => prev.map((t) => (t.key === selectedKey ? res.template : t)))
      setBodyHtml(res.template.body_html)  // re-aplica sanitized version del backend
      setSavedMessage('Plantilla guardada.')
      setTimeout(() => setSavedMessage(null), 3500)
    } catch (err) {
      setSaveError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo guardar.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleSendTest() {
    setTestResult(null)
    setTestError(null)
    if (testTo.trim() === '') {
      setTestError('Indica un correo destino para la prueba.')
      return
    }
    setTesting(true)
    try {
      await adminClient.emailTemplates.sendTest(selectedKey, testTo.trim())
      setTestResult(`Correo de prueba enviado a ${testTo.trim()} con valores de ejemplo.`)
    } catch (err) {
      setTestError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo enviar.')
    } finally {
      setTesting(false)
    }
  }

  if (loadError) return <section className="card"><p className="error">{loadError}</p></section>
  if (loading) return <section className="card"><p className="muted">Cargando plantillas…</p></section>

  return (
    <section className="card">
      <h3>Plantillas de correo</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Edita el contenido de los correos automáticos. Usa el botón <strong>Insertar variable</strong>{' '}
        para añadir valores dinámicos (no los escribas como texto — el chip se sustituye al enviar).
      </p>

      <div className="tabs" style={{ marginTop: '.75rem' }}>
        {items.map((t) => (
          <button key={t.key} type="button"
                  className={selectedKey === t.key ? 'tab-button active' : 'tab-button'}
                  onClick={() => handleSelect(t.key)}>
            {EMAIL_TEMPLATE_LABELS[t.key]}
          </button>
        ))}
      </div>

      {selected && (
        <form onSubmit={handleSave} className="form-grid" noValidate style={{ marginTop: '1rem' }}>
          <label className="field">
            <span>Asunto</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)}
                   required maxLength={255} disabled={submitting} />
          </label>

          <label className="field">
            <span>Contenido HTML</span>
            <Suspense fallback={<p className="muted">Cargando editor…</p>}>
              <EmailTemplateEditor
                value={bodyHtml}
                onChange={setBodyHtml}
                variables={selected.variables}
                disabled={submitting}
                minHeight="280px"
              />
            </Suspense>
          </label>

          {saveError && <p className="error">{saveError}</p>}
          {savedMessage && <p className="info">{savedMessage}</p>}

          <div className="modal__actions">
            <button type="submit" className="primary-btn" disabled={submitting}>
              {submitting ? 'Guardando…' : 'Guardar plantilla'}
            </button>
          </div>
        </form>
      )}

      <hr style={{ margin: '1.5rem 0', borderColor: 'var(--border)' }} />

      <h4>Enviar plantilla de prueba</h4>
      <p className="muted" style={{ marginTop: 0 }}>
        Envía esta plantilla a un correo, con valores de ejemplo en las variables.
      </p>
      <div className="form-grid">
        <label className="field">
          <span>Correo destino</span>
          <div className="password-row">
            <input value={testTo} onChange={(e) => setTestTo(e.target.value)}
                   type="email" placeholder="prueba@example.com" disabled={testing} className="mono" />
            <button type="button" className="ghost-btn" onClick={handleSendTest} disabled={testing}>
              {testing ? 'Enviando…' : 'Enviar prueba'}
            </button>
          </div>
        </label>
        {testError && <p className="error">{testError}</p>}
        {testResult && <p className="info">{testResult}</p>}
      </div>
    </section>
  )
}
